import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../hooks/useTheme';
import { loadStripe } from '@stripe/stripe-js';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Button from '../common/Button';
import { createPaymentIntent, createSubscription } from '../../services/subscriptionService';

// Styled components for payment form
const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-primary);

  @media (prefers-color-scheme: dark) {
    color: #f5f5f5;
  }
`;

// Add global styles for the zip code input placeholder
const GlobalStyle = styled.div`
  .zip-code-input {
    &::placeholder {
      color: var(--text-tertiary, #aab7c4);
    }

    @media (prefers-color-scheme: dark) {
      &::placeholder {
        color: #aab7c4;
      }
    }
  }
`;

const CardContainer = styled.div`
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--background-primary);
  transition: all 0.2s ease;

  &:focus-within {
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(var(--accent-primary-rgb), 0.2);
  }
`;

const CardElementContainer = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  position: relative;

  .StripeElement {
    width: 100%;
    padding: 0.5rem 0;
  }
`;

const PaymentStatus = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  text-align: center;
  font-size: 0.875rem;

  &.error {
    background-color: rgba(229, 57, 53, 0.1);
    color: #e53935;

    @media (prefers-color-scheme: dark) {
      background-color: rgba(229, 57, 53, 0.2);
      color: #ff6b6b;
    }
  }

  &.processing {
    background-color: rgba(3, 169, 244, 0.1);
    color: #0288d1;

    @media (prefers-color-scheme: dark) {
      background-color: rgba(3, 169, 244, 0.2);
      color: #4fc3f7;
    }
  }

  &.success {
    background-color: rgba(67, 160, 71, 0.1);
    color: #43a047;

    @media (prefers-color-scheme: dark) {
      background-color: rgba(67, 160, 71, 0.2);
      color: #81c784;
    }
  }
`;

// Initialize Stripe
export const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Add a console log to check if the publishable key is available
console.log("Stripe Publishable Key available:", !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Payment Form Component
const PaymentForm = ({ email, organizationName, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentStatus, setPaymentStatus] = useState('');
  const [statusClass, setStatusClass] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [cardComplete, setCardComplete] = useState(false);

  // Ref to track if payment intent has been created
  const paymentIntentCreatedRef = useRef(false);

  // Use the theme hook to ensure theme persistence
  const { theme } = useTheme();

  // Card element options with theme-aware color
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: theme === 'dark' ? '#ffffff' : 'var(--text-primary, #424770)',
        '::placeholder': {
          color: theme === 'dark' ? '#aab7c4' : 'var(--text-tertiary, #aab7c4)',
        },
      },
      invalid: {
        color: '#e53935',
        iconColor: '#e53935',
      },
    },
  };

  useEffect(() => {
    // Create a payment intent when the component mounts, but only once
    if (email && organizationName && !clientSecret && !paymentIntentCreatedRef.current) {
      console.log('Calling createPaymentIntent with email and organizationName:', email, organizationName);
      paymentIntentCreatedRef.current = true; // Mark as created before the async call
      createPaymentIntentHandler();
    } else {
      console.log('Not calling createPaymentIntent because:', {
        email: !!email,
        organizationName: !!organizationName,
        clientSecretAlreadySet: !!clientSecret,
        alreadyCreated: paymentIntentCreatedRef.current
      });
    }
  }, [email, organizationName, clientSecret]);

  // Add a separate effect to check if stripe is loaded
  useEffect(() => {
    if (!stripe) {
      console.log("Stripe.js hasn't loaded yet. The button will be disabled until it's ready.");
    } else {
      console.log("Stripe.js has loaded successfully.");
    }
  }, [stripe]);

  const createPaymentIntentHandler = async () => {
    try {
      setIsProcessing(true);
      setPaymentStatus('Creating subscription...');
      setStatusClass('processing');

      const data = await createPaymentIntent({
        email,
        organizationName
      });

      setClientSecret(data.clientSecret);
      setPaymentIntentId(data.paymentIntentId);
      setSubscriptionId(data.subscriptionId);
      setPaymentStatus('Ready for payment');
      setStatusClass('');
    } catch (error) {
      console.error('Error creating subscription:', error);
      setPaymentStatus(`Error: ${error.message}`);
      setStatusClass('error');
      onError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    console.log('Payment form submitted with state:', {
      stripeLoaded: !!stripe,
      elementsLoaded: !!elements,
      clientSecret: !!clientSecret,
      cardComplete,
      zipCode: !!zipCode
    });

    if (!stripe || !elements || !clientSecret) {
      console.error('Cannot process payment: stripe, elements, or clientSecret is missing');
      return;
    }

    if (!cardComplete) {
      console.error('Cannot process payment: card details are incomplete');
      setPaymentStatus('Please complete your card details');
      setStatusClass('error');
      return;
    }

    if (!zipCode) {
      console.error('Cannot process payment: ZIP code is missing');
      setPaymentStatus('Please enter your ZIP code');
      setStatusClass('error');
      return;
    }

    setIsProcessing(true);
    setPaymentStatus('Processing payment...');
    setStatusClass('processing');

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);
      console.log('Confirming card setup with client secret:', clientSecret.substring(0, 10) + '...');

      // For subscriptions, we should use confirmCardSetup instead of confirmCardPayment
      // as the backend is returning a SetupIntent client secret
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            email: email,
            address: {
              postal_code: zipCode
            }
          },
        },
      });

      if (error) {
        console.error('Payment confirmation error:', error);
        throw error;
      }

      console.log('Setup intent result:', setupIntent);

      if (setupIntent.status === 'succeeded') {
        setPaymentStatus('Card added successfully! Creating subscription...');
        setStatusClass('processing');

        try {
          const data = await createSubscription({
            email,
            organizationName,
            paymentMethod: setupIntent.payment_method
          });

          setPaymentStatus('Subscription activated! Setting up your account...');
          setStatusClass('success');

          // Pass subscription ID to onSuccess callback
          onSuccess(data.subscriptionId || subscriptionId);
        } catch (error) {
          console.error('Error creating subscription:', error);
          setPaymentStatus(`Error: ${error.message}`);
          setStatusClass('error');
          onError(error.message);
          return;
        }
      } else {
        setPaymentStatus(`Payment status: ${setupIntent.status}`);
        setStatusClass('processing');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus(`Error: ${error.message}`);
      setStatusClass('error');
      onError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <GlobalStyle />
      <FormGroup>
        <Label>
          <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '8px' }} />
          Card Number
        </Label>
        <CardContainer>
          <CardElementContainer>
            <CardNumberElement 
              options={cardElementOptions} 
              onChange={(event) => {
                setCardComplete(event.complete);
              }}
            />
          </CardElementContainer>
        </CardContainer>
      </FormGroup>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <FormGroup style={{ flex: 1 }}>
          <Label>Expiration Date</Label>
          <CardContainer>
            <CardElementContainer>
              <CardExpiryElement options={cardElementOptions} />
            </CardElementContainer>
          </CardContainer>
        </FormGroup>

        <FormGroup style={{ flex: 1 }}>
          <Label>CVC</Label>
          <CardContainer>
            <CardElementContainer>
              <CardCvcElement options={cardElementOptions} />
            </CardElementContainer>
          </CardContainer>
        </FormGroup>
      </div>

      <FormGroup>
        <Label>ZIP Code</Label>
        <CardContainer>
          <CardElementContainer>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              placeholder="Enter ZIP code"
              className="zip-code-input"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                background: 'transparent',
                color: theme === 'dark' ? '#ffffff' : 'var(--text-primary, #424770)',
                fontSize: '16px',
                padding: '0.5rem 0',
                outline: 'none',
              }}
            />
          </CardElementContainer>
        </CardContainer>
      </FormGroup>

      {paymentStatus && (
        <PaymentStatus className={statusClass}>
          {paymentStatus}
        </PaymentStatus>
      )}

      <Button
        variant="primary"
        type="submit"
        fullWidth
        disabled={!stripe || !clientSecret || isProcessing || !cardComplete || !zipCode}
      >
        {isProcessing ? 'Processing...' : 
         !stripe ? 'Loading payment system...' : 
         !clientSecret ? 'Preparing payment...' : 
         !cardComplete ? 'Please complete card details' : 
         !zipCode ? 'Please enter ZIP code' : 
         'Subscribe Now'}
      </Button>
    </form>
  );
};

export default PaymentForm;
